import { format, isToday, isYesterday } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProviderMessageBubble } from "./ProviderMessageBubble";
import styles from "./ProviderMessageList.module.css";

interface Message {
	id: string;
	content: string;
	senderName: string;
	senderType: "patient" | "provider";
	senderId: string;
	createdAt: Date;
	status?: "sent" | "delivered" | "read";
}

interface ProviderMessageListProps {
	messages: Message[];
	currentUserId: string;
	isLoading?: boolean;
	onLoadMore?: () => void;
	hasMore?: boolean;
	typingUsers?: string[];
}

export function ProviderMessageList({
	messages,
	currentUserId,
	isLoading = false,
	onLoadMore,
	hasMore = false,
	typingUsers = [],
}: ProviderMessageListProps) {
	const { t } = useTranslation("chat");
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);

	// Auto-scroll to bottom for new messages - LIMITED to chat container only
	useEffect(() => {
		if (shouldScrollToBottom && messagesContainerRef.current) {
			// Scroll within the chat container only, not the entire page
			const container = messagesContainerRef.current;
			container.scrollTop = container.scrollHeight;
		}
	}, [shouldScrollToBottom]);

	// Handle scroll events for auto-scroll behavior and pagination
	useEffect(() => {
		const container = messagesContainerRef.current;
		if (!container) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
			setShouldScrollToBottom(isNearBottom);

			// Load more messages when scrolled to top
			if (scrollTop === 0 && hasMore && onLoadMore && !isLoading) {
				onLoadMore();
			}
		};

		container.addEventListener("scroll", handleScroll);
		return () => container.removeEventListener("scroll", handleScroll);
	}, [hasMore, onLoadMore, isLoading]);

	const groupMessagesByDate = (messages: Message[]) => {
		const groups: { date: string; messages: Message[] }[] = [];
		let currentDate = "";
		let currentGroup: Message[] = [];

		for (const message of messages) {
			const messageDate = format(message.createdAt, "yyyy-MM-dd");

			if (messageDate !== currentDate) {
				if (currentGroup.length > 0) {
					groups.push({ date: currentDate, messages: currentGroup });
				}
				currentDate = messageDate;
				currentGroup = [message];
			} else {
				currentGroup.push(message);
			}
		}

		if (currentGroup.length > 0) {
			groups.push({ date: currentDate, messages: currentGroup });
		}

		return groups;
	};

	const formatDateHeader = (dateString: string) => {
		const date = new Date(dateString);
		if (isToday(date)) return "Today";
		if (isYesterday(date)) return "Yesterday";
		return format(date, "MMMM d, yyyy");
	};

	const messageGroups = groupMessagesByDate(messages);

	return (
		<div
			ref={messagesContainerRef}
			className={styles.container}
			data-scrollable="true"
		>
			{/* Loading indicator for load more */}
			{isLoading && hasMore && (
				<div className={styles.loadingContainer}>
					<div className={styles.loadingSpinner} />
					<span className={styles.loadingText}>{t("loadingMessages")}</span>
				</div>
			)}

			{/* Message groups by date */}
			{messageGroups.map((group) => (
				<div key={group.date}>
					{/* Date header */}
					<div className={styles.dateHeader}>
						<span className={styles.dateLabel}>
							{formatDateHeader(group.date)}
						</span>
					</div>

					{/* Messages for this date */}
					{group.messages.map((message) => (
						<ProviderMessageBubble
							key={message.id}
							id={message.id}
							content={message.content}
							senderName={message.senderName}
							senderType={message.senderType}
							createdAt={message.createdAt}
							isOwn={message.senderId === currentUserId}
							status={message.status}
						/>
					))}
				</div>
			))}

			{/* Typing indicators */}
			{typingUsers.length > 0 && (
				<div className={styles.typingContainer}>
					<div className={styles.typingBubble}>
						<div className={styles.typingDots}>
							<div className={styles.typingDot} />
							<div
								className={`${styles.typingDot} ${styles.typingDotDelay1}`}
							/>
							<div
								className={`${styles.typingDot} ${styles.typingDotDelay2}`}
							/>
						</div>
						<span className={styles.typingText}>
							{typingUsers.length === 1
								? `${typingUsers[0]} is typing...`
								: `${typingUsers.length} people are typing...`}
						</span>
					</div>
				</div>
			)}

			{/* Empty state */}
			{messages.length === 0 && !isLoading && (
				<div className={styles.emptyState}>
					<div className={styles.emptyText}>
						<p>No messages yet.</p>
						<p>Start the conversation!</p>
					</div>
				</div>
			)}

			<div ref={messagesEndRef} />
		</div>
	);
}
